var _excluded = ["disabled", "fullWidth", "helperText", "helperTextPlacement", "label", "required", "showError", "errorMessage", "children", "edges", "name", "minYear", "maxYear", "defaultYear", "onChange", "value"];
function _extends() { _extends = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends.apply(this, arguments); }
function _objectWithoutPropertiesLoose(source, excluded) { if (source == null) return {}; var target = {}; var sourceKeys = Object.keys(source); var key, i; for (i = 0; i < sourceKeys.length; i++) { key = sourceKeys[i]; if (excluded.indexOf(key) >= 0) continue; target[key] = source[key]; } return target; }
import React, { useState, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { makeStyles } from '@material-ui/core/styles';
import Grid from '@material-ui/core/Grid';
import FormControl from '../FormControl';
import DateNumberInput from './DateNumberInput';
var isLeapYear = function isLeapYear(year) {
  return year % 4 === 0 && (year % 100 !== 0 || year % 400 === 0);
};
var useStyles = makeStyles(function (theme) {
  return {
    root: {
      flexWrap: 'nowrap'
    },
    day: {
      flexGrow: 0.5,
      minWidth: 80
    },
    month: {
      flexGrow: 0.5,
      minWidth: 80
    },
    year: {
      flexGrow: 2,
      minWidth: 110
    },
    slash: {
      color: theme.palette.default.light
    }
  };
});
var DateInput = function DateInput(_ref) {
  var disabled = _ref.disabled,
    fullWidth = _ref.fullWidth,
    helperText = _ref.helperText,
    helperTextPlacement = _ref.helperTextPlacement,
    label = _ref.label,
    required = _ref.required,
    showError = _ref.showError,
    errorMessage = _ref.errorMessage,
    children = _ref.children,
    edges = _ref.edges,
    name = _ref.name,
    minYear = _ref.minYear,
    maxYear = _ref.maxYear,
    defaultYear = _ref.defaultYear,
    onChange = _ref.onChange,
    value = _ref.value,
    rest = _objectWithoutPropertiesLoose(_ref, _excluded);
  var _useState = useState(31),
    maxDays = _useState[0],
    setMaxDays = _useState[1];
  var classes = useStyles();
  var dayInput = useRef(null);
  var monthInput = useRef(null);
  var yearInput = useRef(null);
  var handleDayChange = function handleDayChange(day) {
    onChange(_extends({}, value, {
      day: day
    }));
  };
  var handleMonthChange = function handleMonthChange(month) {
    onChange(_extends({}, value, {
      month: month
    }));
  };
  var handleYearChange = function handleYearChange(year) {
    onChange(_extends({}, value, {
      year: year
    }));
  };
  var day = value.day,
    month = value.month,
    year = value.year;
  useEffect(function () {
    var hasThirtyDays = [4, 6, 9, 11];
    if (parseInt(month, 10) === 2) {
      setMaxDays(isLeapYear(year) ? 29 : 28);
    } else if (hasThirtyDays.includes(parseInt(month, 10))) {
      setMaxDays(30);
    } else {
      setMaxDays(31);
    }
  }, [month, year]);
  return /*#__PURE__*/React.createElement(FormControl, {
    fullWidth: fullWidth,
    name: name,
    label: label,
    helperText: helperText,
    helperTextPlacement: helperTextPlacement,
    disabled: disabled,
    required: required,
    showError: showError,
    errorMessage: errorMessage,
    edges: edges
  }, /*#__PURE__*/React.createElement("input", {
    name: name,
    style: {
      height: 0,
      width: 0,
      appearance: 'none',
      border: 'none',
      outline: 'none',
      margin: 0,
      padding: 0
    }
  }), /*#__PURE__*/React.createElement(Grid, {
    container: true,
    spacing: 2,
    className: classes.root,
    alignItems: "center"
  }, /*#__PURE__*/React.createElement(Grid, {
    item: true,
    className: classes.day
  }, /*#__PURE__*/React.createElement(DateNumberInput, {
    disabled: disabled,
    min: 1,
    max: maxDays,
    placeholder: "DD",
    leadingZero: true,
    onChange: handleDayChange,
    value: day,
    name: name + "_day",
    inputRef: dayInput,
    required: required,
    label: "Day",
    hideLabel: true
  })), /*#__PURE__*/React.createElement(Grid, {
    item: true,
    className: classes.slash
  }, "/"), /*#__PURE__*/React.createElement(Grid, {
    item: true,
    className: classes.month
  }, /*#__PURE__*/React.createElement(DateNumberInput, {
    disabled: disabled,
    min: 1,
    max: 12,
    placeholder: "MM",
    leadingZero: true,
    onChange: handleMonthChange,
    value: month,
    name: name + "_month",
    inputRef: monthInput,
    required: required,
    label: "Month",
    hideLabel: true
  })), /*#__PURE__*/React.createElement(Grid, {
    item: true,
    className: classes.slash
  }, "/"), /*#__PURE__*/React.createElement(Grid, {
    item: true,
    className: classes.year
  }, /*#__PURE__*/React.createElement(DateNumberInput, {
    disabled: disabled,
    min: parseInt(minYear, 10),
    max: parseInt(maxYear, 10),
    placeholder: "YYYY",
    leadingZero: false,
    onChange: handleYearChange,
    value: value.year,
    name: name + "_year",
    inputRef: yearInput,
    required: required,
    label: "Year",
    reverse: true,
    hideLabel: true
  }))));
};
DateInput.propTypes = process.env.NODE_ENV !== "production" ? {
  /**
   * @ignore
   */
  children: PropTypes.any,
  /**
   * Function to handle input change
   */
  onChange: PropTypes.func.isRequired,
  /**
   * Function to handle input change
   */
  value: PropTypes.shape({
    day: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    month: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    year: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
  }),
  /**
   * If `true`, the label, input and helper text should be displayed in a disabled state.
   */
  disabled: PropTypes.bool,
  /**
   * Whether or not to display an error message (if there is a message to display).
   */
  showError: PropTypes.bool,
  /**
   * The error message to display if showError === true
   */
  errorMessage: PropTypes.string,
  /**
   * Minimun year
   */
  minYear: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  /**
   * Maximum Year
   */
  maxYear: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  /**
   * If `true`, the component will take up the full width of its container.
   */
  fullWidth: PropTypes.bool,
  /**
   * The helper text content.
   */
  helperText: PropTypes.node,
  /**
   * Placed the helper text above or below the input.
   */
  helperTextPlacement: PropTypes.oneOf(['above', 'below']),
  /**
   * The label content.
   */
  label: PropTypes.node,
  /**
   * The input name.
   */
  name: PropTypes.string.isRequired,
  /**
   * If `true`, the label will indicate that the input is required.
   */
  required: PropTypes.bool,
  /**
   * Make button edges rounded or square
   */
  edges: PropTypes.oneOf(['normal', 'rounded', 'sharp']),
  /**
   * Default year for the year select
   */
  defaultYear: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
} : {};
DateInput.defaultProps = {
  children: null,
  disabled: false,
  fullWidth: false,
  helperText: '',
  helperTextPlacement: 'above',
  label: '',
  required: false,
  showError: false,
  errorMessage: '',
  edges: 'normal',
  minYear: 1900,
  maxYear: 2100,
  value: {},
  defaultYear: ''
};
export default DateInput;